/* eslint-disable react-refresh/only-export-components */
// Authentication context provider

import { createContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { fetchCurrentUser, login as apiLogin, logout as apiLogout } from '@/api/auth'
import type { User, LoginRequest, LoginResponse } from '@/api/types'

export interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (credentials: LoginRequest) => Promise<LoginResponse>
  logout: () => Promise<void>
  checkSession: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check session on mount to restore authentication state
  const checkSession = async () => {
    setIsLoading(true)
    try {
      const currentUser = await fetchCurrentUser()
      setUser(currentUser)
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    checkSession()
  }, [])

  // Login function
  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    setIsLoading(true)
    try {
      const response = await apiLogin(credentials)

      if (response.success && response.user) {
        setUser(response.user)
      }

      return response
    } finally {
      setIsLoading(false)
    }
  }

  // Logout function
  const logout = async () => {
    setIsLoading(true)
    try {
      await apiLogout()
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    checkSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
